import React from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES } from "@/data/services";
import { useContactForm } from "@/hooks/useContactForm";

const FIELD =
  "w-full font-body text-[14.5px] text-[#04162E] bg-white border border-[rgba(4,22,46,0.14)] rounded-[2px] px-3.5 py-[13px] placeholder:text-[#8FA0B3] focus:outline-none focus:border-[#0E8A57] focus:ring-2 focus:ring-[#0E8A57] focus:ring-offset-1";
const LABEL = "block font-body text-[12.5px] text-[#5C6C80] mb-2";

const Field = ({ id, label, testId, type = "text", value, onChange }) => (
  <div>
    <label className={LABEL} htmlFor={id}>{label}</label>
    <input id={id} data-testid={testId} type={type} value={value} onChange={onChange} className={FIELD} />
  </div>
);

const Honeypot = ({ value, onChange }) => (
  <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden opacity-0 pointer-events-none" aria-hidden="true">
    <label htmlFor="contact-website">Website</label>
    <input
      id="contact-website"
      name="website"
      data-testid="contact-honeypot"
      type="text"
      tabIndex={-1}
      autoComplete="off"
      value={value}
      onChange={onChange}
    />
  </div>
);

const ServiceSelect = ({ value, onChange }) => (
  <div>
    <label className={LABEL}>Primary Service Needed</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid="contact-select-service"
        className="h-auto font-body text-[14.5px] text-[#04162E] bg-white border-[rgba(4,22,46,0.14)] rounded-[2px] px-3.5 py-[13px] focus:ring-2 focus:ring-[#0E8A57] focus:ring-offset-1 data-[placeholder]:text-[#8FA0B3]"
      >
        <SelectValue placeholder="Select a service" />
      </SelectTrigger>
      <SelectContent className="bg-white border-[rgba(4,22,46,0.14)] text-[#04162E] rounded-[2px] max-h-72">
        {SERVICES.map((s) => (
          <SelectItem key={s.id} value={s.title} data-testid={`service-option-${s.id}`}>
            {s.title}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const ContactForm = ({ serviceInterest, setServiceInterest }) => {
  const f = useContactForm({ serviceInterest, setServiceInterest });

  return (
    <form onSubmit={f.submit} data-testid="contact-form" className="flex flex-col gap-[18px]">
      <Honeypot value={f.honeypot} onChange={(e) => f.setHoneypot(e.target.value)} />

      <div className="grid sm:grid-cols-2 gap-[18px]">
        <Field id="contact-name" label="Full Name *" testId="contact-input-name" value={f.form.full_name} onChange={f.update("full_name")} />
        <Field id="contact-email" label="Corporate Email *" testId="contact-input-email" type="email" value={f.form.email} onChange={f.update("email")} />
      </div>

      <div className="grid sm:grid-cols-2 gap-[18px]">
        <Field id="contact-company" label="Facility / Company *" testId="contact-input-company" value={f.form.company} onChange={f.update("company")} />
        <ServiceSelect value={serviceInterest} onChange={setServiceInterest} />
      </div>

      <div>
        <label className={LABEL} htmlFor="contact-message">Facility Scope / Project Details *</label>
        <textarea
          id="contact-message"
          data-testid="contact-input-message"
          value={f.form.message}
          onChange={f.update("message")}
          rows={5}
          className={`${FIELD} resize-y min-h-[120px]`}
        />
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="contact-consent"
          data-testid="contact-consent-checkbox"
          checked={f.consent}
          onCheckedChange={(v) => f.setConsent(Boolean(v))}
          className="mt-0.5 rounded-[2px] border-[#8FA0B3] data-[state=checked]:bg-[#0E8A57] data-[state=checked]:border-[#0E8A57]"
        />
        <label htmlFor="contact-consent" className="font-body text-[12.5px] leading-[1.55] text-[#5C6C80] cursor-pointer">
          I agree that my information will be handled in accordance with the{" "}
          <Link to="/privacy" className="underline text-[#04162E]">Privacy Policy</Link>, and understand my
          data is stored so PSIEC can respond to my inquiry.
        </label>
      </div>

      <div className="flex items-center gap-[18px] mt-1.5">
        <button
          type="submit"
          disabled={f.submitting}
          data-testid="contact-submit-button"
          className="inline-flex items-center justify-center px-[22px] py-3 text-[14.5px] font-semibold text-white bg-[#04162E] hover:bg-[#12335A] disabled:opacity-60 rounded-[2px] transition-colors"
        >
          {f.submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
            </>
          ) : (
            "Send Inquiry"
          )}
        </button>
        {f.note && (
          <span data-testid="contact-submit-note" className="font-body text-[12.5px] text-[#0B6B44]">
            {f.note}
          </span>
        )}
      </div>
    </form>
  );
};
